import { z } from "zod";

const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50] as const;
const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[1];

function createPageSizeSchema<T extends readonly [number, ...number[]]>(
  options: T
) {
  return z.union(
    options.map((size) => z.literal(size)) as [
      z.ZodLiteral<T[0]>,
      ...z.ZodLiteral<T[number]>[],
    ]
  );
}

const PageSizeSchema = createPageSizeSchema(PAGE_SIZE_OPTIONS);

function createPaginationStateSchema<T extends readonly [number, ...number[]]>(
  options: T
) {
  return z.object({
    pageIndex: z.number().int().min(0),
    pageSize: createPageSizeSchema(options),
  });
}

const PaginationStateSchema = z.object({
  pageIndex: z.number().int().min(0),
  pageSize: PageSizeSchema,
});

const PaginationMetaSchema = z.object({
  page: z.number().int().positive(),
  // z.number().int().positive() rather than PageSizeSchema: API responses from
  // routes using custom pageSizeOptions (e.g. OCPP logs) return values outside
  // the default [10,20,30,40,50] set, so the structural shape must accept any
  // positive integer.
  pageSize: z.number().int().positive(),
  totalCount: z.number().int().min(0),
  totalPages: z.number().int().min(0),
});

const PaginationLinksSchema = z.object({
  next: z.string().optional(),
  current: z.string(),
  prev: z.string().optional(),
});

const PaginationSearchSchemaBase = z.object({
  limit: PageSizeSchema.default(DEFAULT_PAGE_SIZE).transform(String),
});

const PaginationOffsetSearchSchema = PaginationSearchSchemaBase.extend({
  offset: z.number().min(0).default(0).transform(String),
});

const PaginationPageSearchSchema = PaginationSearchSchemaBase.extend({
  page: z.number().min(1).transform(String),
});

const PaginationCursorSearchSchema = PaginationSearchSchemaBase.extend({
  cursor: z.string().optional(),
});

const PaginationCursorMetaSchema = z.object({
  hasNext: z.boolean(),
  hasPrevious: z.boolean(),
  nextCursor: z.string().optional(),
  prevCursor: z.string().optional(),
});

const PaginatedResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    data: z.array(dataSchema),
    meta: PaginationMetaSchema,
  });

const PaginatedCursorResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    data: z.array(dataSchema),
    pagination: PaginationCursorMetaSchema,
  });

type PageSize<
  T extends readonly [number, ...number[]] = typeof PAGE_SIZE_OPTIONS,
> = T[number];
type PageSizeOptions = readonly [number, ...number[]];
type PaginationState = z.infer<typeof PaginationStateSchema>;
type PaginationMeta = z.infer<typeof PaginationMetaSchema>;
type PaginationSearchBase = z.input<typeof PaginationSearchSchemaBase>;
type PaginationOffsetSearch = z.input<typeof PaginationOffsetSearchSchema>;
type PaginationPageSearch = z.input<typeof PaginationPageSearchSchema>;
type PaginationCursorMeta = z.infer<typeof PaginationCursorMetaSchema>;
type PaginatedResponse<T> = {
  data: T[];
  meta: PaginationMeta;
};
type PaginationLinks = z.infer<typeof PaginationLinksSchema>;

export {
  createPageSizeSchema,
  createPaginationStateSchema,
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  PageSizeSchema,
  PaginationStateSchema,
  PaginationMetaSchema,
  PaginationOffsetSearchSchema,
  PaginationPageSearchSchema,
  PaginationCursorSearchSchema,
  PaginationCursorMetaSchema,
  PaginationSearchSchemaBase,
  PaginationLinksSchema,
  PaginatedResponseSchema,
  PaginatedCursorResponseSchema,
};

export type {
  PageSize,
  PageSizeOptions,
  PaginationState,
  PaginationMeta,
  PaginationSearchBase,
  PaginationOffsetSearch,
  PaginationPageSearch,
  PaginationCursorMeta,
  PaginatedResponse,
  PaginationLinks,
};
